// components/SecureImage.jsx
import React, { useEffect, useState, useRef } from 'react';
import authAxios from '../utils/authAxios';
import { secureImageLoader } from '../utils/secureImageLoader';
import { getImageFromCache, storeImageInCache } from '../utils/imageCache';

const CDN_BASE = 'https://thevaultmedia.azureedge.net/vault-images';
// Recognize both legacy generic names and current container folder names
const KNOWN_FOLDERS = new Set([
  'methods', 'bags', 'plu',
  'pidmethods', 'pidbags', 'pidplu',
]);

/**
 * Extract just the filename from `src`:
 *  - Accepts plain filename ("CPK.png")
 *  - Foldered ("methods/CPK.png") -> returns "CPK.png"
 *  - Full CDN or blob URL -> returns "CPK.png"
 *  - Strips query/hash and trims whitespace
 */
function normalizeFilename(src, folder) {
  if (!src) return '';

  // Strip any URL origin and leading path up to /vault-images/
  try {
    // If it's a URL, use URL parser
    const maybeUrl = new URL(src);
    src = maybeUrl.pathname + (maybeUrl.search || '') + (maybeUrl.hash || '');
  } catch {
    // not a full URL, continue
  }

  // Remove everything before and including .../vault-images/
  src = src.replace(/^[^?&#]*\/vault-images\//i, '');

  // Remove any *one* folder segment if present (methods|bags|plu or any folder)
  const parts = src.split('?')[0].split('#')[0].split('/').filter(Boolean);
  if (parts.length > 1) {
    // If first segment matches a known folder OR any single-segment folder,
    // drop it so we always control folder on the caller prop.
    const [maybeFolder, ...rest] = parts;
    src = (KNOWN_FOLDERS.has(maybeFolder.toLowerCase()) ? rest : parts).join('/');
  } else {
    src = parts.join('/');
  }

  // Decode, trim, and strip query/hash again (safety)
  try { src = decodeURIComponent(src); } catch {}
  src = src.trim().replace(/\s+/g, ' ');
  src = src.split('?')[0].split('#')[0];

  // Ensure just the last path segment (filename)
  const segs = src.split('/').filter(Boolean);
  const filename = segs.length ? segs[segs.length - 1] : '';

  return filename;
}

function buildCdnUrl(folder, filename) {
  const safeFolder = String(folder || '').trim(); // preserve case to match CDN paths
  const base = `${CDN_BASE}/${safeFolder}`;
  // Encode each path segment but keep dot for extensions
  // Replace spaces with %20 and let encodeURIComponent handle the rest
  const encoded = encodeURIComponent(filename).replace(/%2F/g, '/');
  return `${base}/${encoded}`;
}

const SecureImage = ({ src, folder, alt = '', style = {}, ...props }) => {
  const [imageUrl, setImageUrl] = useState(null);
  const previousKeyRef = useRef('');
  const revokeRef = useRef(null); // track object URLs to revoke on unmount

  useEffect(() => {
    let cancelled = false;

    if (!src || !folder) return;

    // If an absolute URL is provided and it's NOT our CDN host, use it directly
    try {
      const u = new URL(src);
      const cdnHost = new URL(CDN_BASE).host;
      if (u.host !== cdnHost) {
        if (!cancelled) setImageUrl(src);
        return () => { cancelled = true; };
      }
    } catch { /* not an absolute URL */ }

    const filename = normalizeFilename(src, folder);
    if (!filename) return;

    const cacheKey = `${folder}/${filename}`;
    if (previousKeyRef.current === cacheKey) return;
    previousKeyRef.current = cacheKey;

    const tryLoad = (nameToTry) => {
      const cdnUrl = buildCdnUrl(folder, nameToTry);
      const probe = new Image();

      probe.onload = () => {
        if (cancelled) return;
        setImageUrl(cdnUrl);
      };

      probe.onerror = async () => {
        if (cancelled) return;

        // 1) Cache hit? (primed or blob)
        const cached = getImageFromCache(cacheKey);
        if (cached) {
          setImageUrl(cached);
          return;
        }

        // 2) Small retry: if filename has accidental leading/trailing spaces removed above,
        //    also try collapsing double spaces (already handled) or replacing spaces with underscores (optional).
        //    (Skip if no spaces)
        if (/\s/.test(nameToTry)) {
          const underscored = nameToTry.replace(/\s+/g, '_');
          if (underscored !== nameToTry) {
            tryLoad(underscored);
            return;
          }
        }

        // 3) Auth’d blob fallback
        try {
          const blob = await secureImageLoader(authAxios, folder, filename);
          const objectUrl = URL.createObjectURL(blob);
          revokeRef.current = objectUrl;
          storeImageInCache(cacheKey, objectUrl);
          if (!cancelled) setImageUrl(objectUrl);
        } catch (err) {
          console.error('[SecureImage] blob fallback failed:', err);
          if (!cancelled) setImageUrl(null);
        }
      };

      probe.src = cdnUrl;
    };

    // Start with cleaned filename
    tryLoad(filename);

    return () => {
      cancelled = true;
      if (revokeRef.current) {
        URL.revokeObjectURL(revokeRef.current);
        revokeRef.current = null;
      }
    };
  }, [src, folder]);

  return imageUrl ? (
    <img
      src={imageUrl}
      alt={alt}
      style={style}
      loading="lazy"
      {...props}
    />
  ) : (
    <div style={{ display: 'inline-block', ...style }} title="Image not available">⚠️</div>
  );
};

export default SecureImage;
